import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import { useAuth } from '@/hooks/useAuth';
import { apiClient } from '@/services/api';
import { FormCard } from '@/components/ui/FormCard';
import { CustomSelect, type SelectOption } from '@/components/ui';
import { PasswordInput } from '@/components/PasswordInput';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';
import { VERBATIM_INPUT_PROPS } from '@/constants';

interface UserEditFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country: SelectOption | null;
  city: SelectOption | null;
  addressLine1: string;
  addressLine2: string;
  postalCode: string;
}

interface UserEditFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
}

// ISO code lookup: country name → isoCode (populated from API)
const isoCodeByName = new Map<string, string>();

export const UserEditForm: React.FC<UserEditFormProps> = ({ onSuccess, onCancel, onClose }) => {
  const { t } = useTranslation(['account', 'common']);
  const { user, setUser, bindEmail } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [bindPassword, setBindPassword] = useState('');
  const [bindPasswordConfirm, setBindPasswordConfirm] = useState('');

  const isTelegramOnly = !user?.email;

  // Country options loaded from backend API
  const [countryOptions, setCountryOptions] = useState<SelectOption[]>([]);
  const [countriesLoading, setCountriesLoading] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
    setValue,
  } = useForm<UserEditFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      country: null,
      city: null,
      addressLine1: '',
      addressLine2: '',
      postalCode: '',
    },
  });

  const selectedCountry = watch('country');
  const watchedEmail = watch('email');
  const showBindPasswordFields = isTelegramOnly && watchedEmail.trim().length > 0;

  // Load countries from backend API on mount
  useEffect(() => {
    setCountriesLoading(true);
    apiClient.getCountries().then((response) => {
      const countries = response.data?.data || [];
      const options: SelectOption[] = countries.map((c: { name: string; isoCode: string; flag: string }) => ({
        value: c.name,
        label: `${c.flag} ${c.name}`,
      }));
      setCountryOptions(options);
      isoCodeByName.clear();
      countries.forEach((c: { name: string; isoCode: string }) => isoCodeByName.set(c.name, c.isoCode));
      setCountriesLoading(false);
    }).catch(() => {
      setCountriesLoading(false);
    });
  }, []);

  // Load city options from backend API when country changes
  const [cityOptions, setCityOptions] = useState<SelectOption[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(false);

  useEffect(() => {
    if (!selectedCountry) {
      setCityOptions([]);
      return;
    }
    const isoCode = isoCodeByName.get(selectedCountry.value);
    if (!isoCode) {
      setCityOptions([]);
      return;
    }

    setCitiesLoading(true);
    apiClient.getCities(isoCode).then((response) => {
      const cities = response.data?.data || [];
      const options: SelectOption[] = cities.map((c: { name: string }) => ({
        value: c.name,
        label: c.name,
      }));
      setCityOptions(options);
      setCitiesLoading(false);
    }).catch(() => {
      setCitiesLoading(false);
    });
  }, [selectedCountry]);

  // Populate form from user data (wait for country options to load)
  useEffect(() => {
    if (user && countryOptions.length > 0) {
      const userCountry = countryOptions.find((c) => c.value === user.country) || null;
      reset({
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        country: userCountry,
        city: null,
        addressLine1: user.address_line1 || '',
        addressLine2: user.address_line2 || '',
        postalCode: user.postal_code || '',
      });
    }
  }, [user, reset, countryOptions]);

  // Set city value after cityOptions are computed (depends on country being set first)
  useEffect(() => {
    if (user?.city && cityOptions.length > 0) {
      const userCity = cityOptions.find((c) => c.value === user.city);
      if (userCity) {
        setValue('city', userCity);
      }
    }
  }, [cityOptions, user, setValue]);

  const onValidationError = (errors: Record<string, any>) => {
    const firstError = Object.values(errors)[0];
    const message = firstError?.message || t('account:fillRequiredFields');
    toast.error(message);
  };

  const onSubmit = async (data: UserEditFormData) => {
    setIsLoading(true);

    try {
      // TG-only user binding email for the first time
      if (isTelegramOnly && data.email.trim()) {
        if (!bindPassword) {
          toast.error(t('account:enterPasswordForEmail'));
          setIsLoading(false);
          return;
        }
        if (bindPassword !== bindPasswordConfirm) {
          toast.error(t('account:passwordsDoNotMatch'));
          setIsLoading(false);
          return;
        }
        if (bindPassword.length < 6) {
          toast.error(t('account:passwordMinChars'));
          setIsLoading(false);
          return;
        }

        await bindEmail(data.email.trim().toLowerCase(), bindPassword);

        const bindResponse = await apiClient.updateUserProfile({
          first_name: data.firstName.trim(),
          last_name: data.lastName.trim(),
          phone: data.phone.trim() || null,
          country: data.country?.value || null,
          city: data.city?.value || null,
          address_line1: data.addressLine1.trim() || null,
          address_line2: data.addressLine2.trim() || null,
          postal_code: data.postalCode.trim() || null,
        });

        if (bindResponse.data.success && user) {
          const userData = bindResponse.data.data?.user || bindResponse.data.data;
          const updatedUser = { ...user, ...userData };
          setUser(updatedUser);
          localStorage.setItem('currentUser', JSON.stringify(updatedUser));
        }

        toast.success(t('account:emailBoundSuccess'));
        setBindPassword('');
        setBindPasswordConfirm('');
        onSuccess?.();
        return;
      }

      // Regular profile update
      const response = await apiClient.updateUserProfile({
        first_name: data.firstName.trim(),
        last_name: data.lastName.trim(),
        email: data.email.trim().toLowerCase(),
        phone: data.phone.trim() || null,
        country: data.country?.value || null,
        city: data.city?.value || null,
        address_line1: data.addressLine1.trim() || null,
        address_line2: data.addressLine2.trim() || null,
        postal_code: data.postalCode.trim() || null,
      });

      if (response.data.success) {
        const userData = response.data.data?.user || response.data.data;
        const updatedUser = { ...user, ...userData };
        setUser(updatedUser);
        localStorage.setItem('currentUser', JSON.stringify(updatedUser));

        toast.success(response.data.message || t('account:profileUpdated'));

        onSuccess?.();
      }
    } catch (error: any) {
      let errorMessage = t('account:errorUpdatingProfile');
      if (error.response?.status === 409) {
        errorMessage = error.response.data?.message || t('account:emailAlreadyInUse');
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    if (user) {
      const userCountry = countryOptions.find((c) => c.value === user.country) || null;
      const userCity = user.city ? { value: user.city, label: user.city } : null;
      reset({
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        country: userCountry,
        city: userCity,
        addressLine1: user.address_line1 || '',
        addressLine2: user.address_line2 || '',
        postalCode: user.postal_code || '',
      });
      setBindPassword('');
      setBindPasswordConfirm('');
    }
    onCancel?.();
  };

  return (
    <FormCard
      title={t('account:editProfile')}
      subtitle={t('account:updatePersonalInfo')}
      titleSize="sm"
      onClose={onClose}
    >
      <form onSubmit={handleSubmit(onSubmit, onValidationError)}>
        <div className={styles.formFields}>
          {/* Row 1: First Name, Last Name */}
          <div className={styles.fieldRow}>
            <div>
              <input
                className={classNames('field', { 'field--error': errors.firstName })}
                type="text"
                placeholder={t('account:firstName')}
                disabled={isLoading}
                {...register('firstName', { required: t('account:firstNameRequired') })}
              />
              {errors.firstName && (
                <div className={styles.fieldError}>{errors.firstName.message}</div>
              )}
            </div>
            <div>
              <input
                className={classNames('field', { 'field--error': errors.lastName })}
                type="text"
                placeholder={t('account:lastName')}
                disabled={isLoading}
                {...register('lastName')}
              />
            </div>
          </div>

          {/* Row 2: Email, Phone */}
          <div className={styles.fieldRow}>
            <div>
              <input
                className={classNames('field', { 'field--error': errors.email })}
                type="email"
                placeholder={t('account:emailLabel')}
                autoComplete="email"
                {...VERBATIM_INPUT_PROPS}
                disabled={isLoading}
                {...register('email', {
                  required: isTelegramOnly ? false : t('account:emailRequired'),
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: t('account:invalidEmailFormat'),
                  },
                })}
              />
              {isTelegramOnly && !watchedEmail.trim() && (
                <div className={styles.fieldHint}>
                  {t('account:bindEmailHint')}
                </div>
              )}
              {errors.email && (
                <div className={styles.fieldError}>{errors.email.message}</div>
              )}
            </div>
            <div>
              <input
                className={classNames('field', { 'field--error': errors.phone })}
                type="tel"
                placeholder={t('account:phone')}
                disabled={isLoading}
                {...register('phone', {
                  pattern: {
                    value: /^\+?[\d\s\-()]+$/,
                    message: t('account:invalidPhoneFormat'),
                  },
                })}
              />
              {errors.phone && (
                <div className={styles.fieldError}>{errors.phone.message}</div>
              )}
            </div>
          </div>

          {/* Password fields for TG-only users binding email */}
          {showBindPasswordFields && (
            <div className={styles.fieldRow}>
              <div>
                <PasswordInput
                  value={bindPassword}
                  onChange={(e) => setBindPassword(e.target.value)}
                  placeholder={t('account:passwordForEmail')}
                  minLength={6}
                  disabled={isLoading}
                />
              </div>
              <div>
                <PasswordInput
                  value={bindPasswordConfirm}
                  onChange={(e) => setBindPasswordConfirm(e.target.value)}
                  placeholder={t('account:confirmPasswordLabel')}
                  minLength={6}
                  disabled={isLoading}
                />
              </div>
            </div>
          )}

          {/* Row 3: Country, City */}
          <div className={styles.fieldRow}>
            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <CustomSelect
                  options={countryOptions}
                  value={field.value}
                  onChange={(val) => {
                    field.onChange(val);
                    setValue('city', null);
                  }}
                  placeholder={t('account:country')}
                  isSearchable
                  isLoading={countriesLoading}
                  variant="basic"
                  innerRef={field.ref}
                />
              )}
            />
            <Controller
              name="city"
              control={control}
              render={({ field }) => (
                <CustomSelect
                  options={cityOptions}
                  value={field.value}
                  onChange={(val) => {
                    field.onChange(val);
                  }}
                  placeholder={t('account:city')}
                  isSearchable
                  isLoading={citiesLoading}
                  variant="basic"
                  innerRef={field.ref}
                />
              )}
            />
          </div>

          {/* Row 4: Address Line 1, Address Line 2 */}
          <div className={styles.fieldRow}>
            <div>
              <input
                className="field"
                type="text"
                placeholder={t('account:addressLine1')}
                disabled={isLoading}
                {...register('addressLine1')}
              />
            </div>
            <div>
              <input
                className="field"
                type="text"
                placeholder={t('account:addressLine2')}
                disabled={isLoading}
                {...register('addressLine2')}
              />
            </div>
          </div>

          {/* Row 5: Postal Code */}
          <div className={styles.fieldRow}>
            <div>
              <input
                className="field"
                type="text"
                placeholder={t('account:postalCode')}
                disabled={isLoading}
                {...register('postalCode')}
              />
            </div>
            <div />
          </div>
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            className="btn btn--sm btn--outlined"
            onClick={handleReset}
            disabled={isLoading}
          >
            {t('common:cancel')}
          </button>
          <button type="submit" className="btn btn--sm" disabled={isLoading}>
            {t('common:save')}
          </button>
        </div>
      </form>
    </FormCard>
  );
};

export default UserEditForm;
